/**
 * <ck-inquiry> — drives the global "inquiry instead of buy" modal.
 *
 * - Opens the modal from any element carrying [data-ck-inquiry-open], reading the
 *   product title from that element's data-product-title.
 * - Injects the product title into the email subject, mailto link and WhatsApp message.
 * - Copy-to-clipboard for the email address.
 * - Drives a lightweight sticky bar ([data-ck-inquiry-sticky]) by observing the buy
 *   buttons block, so unikat products still get a sticky CTA without the cart-bound
 *   sticky-add-to-cart component.
 */
class CkInquiry extends HTMLElement {
  connectedCallback() {
    this.dialogComponent = this.querySelector('dialog-component');
    this.bodyEl = this.querySelector('[data-ck-inquiry-body]');
    this.subjectEl = this.querySelector('[data-ck-inquiry-subject]');
    this.mailEl = this.querySelector('[data-ck-inquiry-mail]');
    this.whatsappEl = this.querySelector('[data-ck-inquiry-whatsapp]');

    document.addEventListener('click', this.#onDocumentClick);

    const copyBtn = this.querySelector('[data-ck-inquiry-copy]');
    if (copyBtn) copyBtn.addEventListener('click', () => this.#copyEmail(copyBtn));

    this.#setupSticky();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    this.stickyObserver?.disconnect();
  }

  #onDocumentClick = (event) => {
    const trigger = event.target.closest('[data-ck-inquiry-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger.dataset.productTitle || '');
  };

  open(productTitle) {
    this.#applyProductTitle(productTitle);
    const dialog = this.dialogComponent || this.querySelector('dialog-component');
    dialog?.showDialog?.();
  }

  #buildSubject(productTitle) {
    const base = (this.dataset.ckSubject || '').trim();
    const title = (productTitle || '').trim();
    if (base && title) return `${base} – ${title}`;
    return base || title;
  }

  #applyProductTitle(productTitle) {
    const subject = this.#buildSubject(productTitle);

    if (this.subjectEl) this.subjectEl.textContent = subject;

    const email = this.dataset.ckEmail || '';
    if (this.mailEl && email) {
      const body = this.#plainBody();
      const params = new URLSearchParams();
      if (subject) params.set('subject', subject);
      if (body) params.set('body', body);
      const query = params.toString().replace(/\+/g, '%20');
      this.mailEl.setAttribute('href', query ? `mailto:${email}?${query}` : `mailto:${email}`);
    }

    const waNumber = this.dataset.ckWhatsapp || '';
    if (this.whatsappEl && waNumber) {
      const base = `https://wa.me/${waNumber}`;
      this.whatsappEl.setAttribute('href', subject ? `${base}?text=${encodeURIComponent(subject)}` : base);
    }
  }

  /**
   * Converts the rich-text body into plain text with preserved line breaks.
   * Uses innerHTML (not innerText) because the dialog is display:none until opened.
   * @returns {string}
   */
  #plainBody() {
    if (!this.bodyEl) return '';
    const html = this.bodyEl.innerHTML
      .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n');
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  async #copyEmail(button) {
    const email = this.dataset.ckEmail || '';
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
    } catch (error) {
      return;
    }

    const label = button.querySelector('[data-ck-inquiry-copy-label]');
    if (!label) return;
    const original = label.textContent;
    label.textContent = this.dataset.copiedLabel || original;
    setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }

  #setupSticky() {
    const bars = document.querySelectorAll('[data-ck-inquiry-sticky]');
    if (!bars.length) return;

    const buyButtonsBlock = document.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const setStuck = (stuck) => {
      bars.forEach((bar) => {
        bar.querySelector('.sticky-add-to-cart__bar')?.setAttribute('data-stuck', stuck ? 'true' : 'false');
      });
    };

    this.stickyObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      const rect = entry.target.getBoundingClientRect();
      const scrolledPast = !entry.isIntersecting && (rect.bottom < 0 || rect.top < 0);
      setStuck(scrolledPast);
    });

    this.stickyObserver.observe(buyButtonsBlock);
  }
}

if (!customElements.get('ck-inquiry')) {
  customElements.define('ck-inquiry', CkInquiry);
}
